import { Component, ElementRef, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSwitchModule } from 'ng-zorro-antd/switch';

import { BrandingService, CreateBrandPayload, UpdateBrandPayload } from '@services/branding.service';
import { SharedService } from '@services/shared.service';
import {
  BRAND_COLOR_GROUPS,
  BRAND_COLOR_TOKENS,
  BrandColorGroup,
  colorControlName,
} from './branding-tokens';

export interface BrandItem {
  id: number;
  name: string;
  description?: string | null;
  display_name?: string | null;
  primary?: string;
  secondary?: string;
  is_default?: boolean;
  is_active?: boolean;
  brand?: any;
  logo?: any;
  logo_dark?: any;
  icon?: any;
  og_image?: any;
  domains?: any[];
  live_domain_count?: number;
  version?: number;
  created_at?: number;
  updated_at?: number;
  [key: string]: any;
}

export interface DomainItem {
  [x: string]: any;
  id: number;
  branding_id: number;
  hostname: string;
  kind: 'subdomain' | 'custom';
  status: 'live' | 'pending' | 'failed';
  is_primary?: boolean;
  canonical_host?: string;
  dns_setup?: any;
  created_at?: number;
}

@Component({
  selector: 'app-branding',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    NzModalModule,
    NzTableModule,
    NzToolTipModule,
    NzTabsModule,
    NzTagModule,
    NzSelectModule,
    NzSwitchModule,
  ],
  templateUrl: './branding.component.html',
  styleUrls: ['./branding.component.scss'],
})
export class BrandingComponent implements OnInit {
  private fb = inject(FormBuilder);
  private brandingService = inject(BrandingService);
  private translate = inject(TranslateService);
  private cdRef = inject(ChangeDetectorRef);
  sharedService = inject(SharedService);

  @ViewChild('logoFileInput') logoFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('logoDarkFileInput') logoDarkFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('iconFileInput') iconFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('jsonFileInput') jsonFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('editJsonFileInput') editJsonFileInput!: ElementRef<HTMLInputElement>;

  isLoading = false;
  brands: BrandItem[] = [];
  domains: DomainItem[] = [];
  activeTab: 'brands' | 'domains' = 'brands';

  // Brand Modal state
  isBrandModalVisible = false;
  isSavingBrand = false;
  editingBrand: BrandItem | null = null;
  brandForm!: FormGroup;

  // Active theme tab in color picker modal
  colorThemeTab: 'light' | 'dark' = 'light';

  // Domain Modal state
  isDomainModalVisible = false;
  isSavingDomain = false;
  editingDomain: DomainItem | null = null;
  domainForm!: FormGroup;

  // JSON Edit Modal state
  isJsonModalVisible = false;
  isSavingJson = false;
  isExampleJsonModal = false;
  jsonModalBrand: BrandItem | null = null;
  rawJsonString = '';
  jsonError = '';
  isCopied = false;

  // JSON Upload Modal state
  isUploadJsonModalVisible = false;
  isSavingUploadJson = false;
  uploadJsonBrand: BrandItem | null = null;
  uploadedJsonFileName = '';
  uploadedJsonData: any = null;
  uploadJsonError = '';
  isDragging = false;

  // Asset Upload Dropzone Handlers
  selectedFiles: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: File | null } = {};
  uploadedAssetObjects: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: any } = {};
  originalBrandAssets: { [key in 'logo_url' | 'logo_dark_url' | 'icon_url']?: any } = {};

  triggerFileInput(field: 'logo_url' | 'logo_dark_url' | 'icon_url'): void {
    if (field === 'logo_url' && this.logoFileInput) {
      this.logoFileInput.nativeElement.click();
    } else if (field === 'logo_dark_url' && this.logoDarkFileInput) {
      this.logoDarkFileInput.nativeElement.click();
    } else if (field === 'icon_url' && this.iconFileInput) {
      this.iconFileInput.nativeElement.click();
    }
  }

  onFileSelected(event: Event, field: 'logo_url' | 'logo_dark_url' | 'icon_url'): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      this.selectedFiles[field] = file;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const result = e.target.result as string;
        this.brandForm.patchValue({ [field]: result });
        this.brandForm.get(field)?.markAsDirty();
        this.brandForm.get(field)?.markAsTouched();
        this.cdRef.detectChanges();

        // Call baseToImage API with payload { url: result }
        this.sharedService.showLoader();
        this.sharedService.baseToImage(result).subscribe({
          next: (res: any) => {
            this.sharedService.hideLoader();
            if (res) {
              this.uploadedAssetObjects[field] = res;
            }
            this.cdRef.detectChanges();
          },
          error: (err) => {
            this.sharedService.hideLoader();
            console.error('baseToImage failed:', err);
          },
        });
      };
      reader.readAsDataURL(file);
    }
  }

  clearAssetImage(field: 'logo_url' | 'logo_dark_url' | 'icon_url', event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.selectedFiles[field] = null;
    this.uploadedAssetObjects[field] = null;
    this.originalBrandAssets[field] = null;
    this.brandForm.patchValue({ [field]: '' });
    this.brandForm.get(field)?.markAsDirty();
    this.brandForm.get(field)?.markAsTouched();
    this.cdRef.detectChanges();
  }

  verifyingDomainId: number | null = null;

  // DNS Details Modal
  dnsModalVisible = false;
  selectedDnsSetup: any = null;

  ngOnInit(): void {
    this.initForms();
    this.loadData();
  }

  initForms(): void {
    this.brandForm = this.fb.group({
      name: ['', [Validators.required, Validators.maxLength(100)]],
      description: [''],
      is_default: [false],
      is_active: [true],
      // Colours are registered from BRAND_COLOR_GROUPS in addColorControls().
      // Typography
      font_source: ['google'],
      font_family: ['Inter'],
      font_family_ar: ['Vazirmatn'],
      google_family: ['Inter:wght@300;400;500;600;700;800'],
      base_size: [16],
      // Shape
      radius: [12],
      button_radius: [12],
      shell_max: [400],
      // Defaults
      default_theme: ['system'],
      default_language: ['en'],
      // Identity & Meta
      display_name: [''],
      display_name_ar: [''],
      tagline: [''],
      tagline_ar: [''],
      title: [''],
      title_ar: [''],
      meta_description: [''],
      meta_description_ar: [''],
      logo_url: [''],
      logo_dark_url: [''],
      icon_url: [''],
      // Support & Social
      support_email: ['', [Validators.email]],
      support_phone: [''],
      support_whatsapp: [''],
      support_website: [''],
      social_instagram: [''],
      social_x: [''],
      social_youtube: [''],
      social_tiktok: [''],
      social_snapchat: [''],
      social_linkedin: [''],
      powered_by: [true],
      terms_url: [''],
      privacy_url: [''],
    });

    this.domainForm = this.fb.group({
      hostname: ['', [Validators.required, Validators.pattern(/^[a-zA-Z0-9][a-zA-Z0-9-_.]{1,253}\.[a-zA-Z]{2,}$/)]],
      branding_id: [null, [Validators.required]],
      is_primary: [false],
      notes: [''],
    });

    this.addColorControls();
  }

  /* ------------------------- Brand colours (all 28 tokens) ------------------------- */

  readonly colorGroups: BrandColorGroup[] = BRAND_COLOR_GROUPS;

  /** Control name for a token in the currently selected theme tab. */
  colorControl(token: string): string {
    return colorControlName(token, this.colorThemeTab === 'dark');
  }

  /**
   * Register a light and a dark control for every token in the schema.
   * Called once from the constructor so the form and the schema can never drift.
   */
  private addColorControls(): void {
    BRAND_COLOR_TOKENS.forEach((t) => {
      this.brandForm.addControl(
        colorControlName(t.token, false),
        this.fb.control(t.light, t.required ? Validators.required : [])
      );
      this.brandForm.addControl(colorControlName(t.token, true), this.fb.control(t.dark));
    });
  }

  /** Schema defaults for every colour — used when resetting the create form. */
  private defaultColorValues(): Record<string, string> {
    const values: Record<string, string> = {};
    BRAND_COLOR_TOKENS.forEach((t) => {
      values[colorControlName(t.token, false)] = t.light;
      values[colorControlName(t.token, true)] = t.dark;
    });
    return values;
  }

  /**
   * Read every colour out of a saved brand.
   * Legacy documents only carried a handful of keys, and older builds also stored a
   * few of them flat on the brand row, so both shapes are checked before the default.
   */
  private colorValuesFromBrand(colors: any, colorsDark: any, brand: any = {}): Record<string, string> {
    const values: Record<string, string> = {};
    BRAND_COLOR_TOKENS.forEach((t) => {
      values[colorControlName(t.token, false)] = this.ensureHex6(
        colors?.[t.token] ?? brand?.[t.token] ?? brand?.[`${t.token}_color`],
        t.light
      );
      // Falls back to the schema's dark default, never to the light value: a brand with
      // no saved dark colour would otherwise load a light palette into dark mode, which
      // reads as white-on-white and hides the fact that nothing was saved.
      values[colorControlName(t.token, true)] = this.ensureHex6(colorsDark?.[t.token], t.dark);
    });
    return values;
  }

  /** Map an imported JSON colour block onto form controls, ignoring absent keys. */
  private colorPatchFromJson(colors: any, colorsDark: any): Record<string, string> {
    const patch: Record<string, string> = {};
    BRAND_COLOR_TOKENS.forEach((t) => {
      if (colors?.[t.token]) patch[colorControlName(t.token, false)] = colors[t.token];
      if (colorsDark?.[t.token]) patch[colorControlName(t.token, true)] = colorsDark[t.token];
    });
    return patch;
  }

  /** Build a brand.colors / brand.colors_dark block from the form. */
  private buildColorsBlock(val: any, existing: any, dark: boolean): Record<string, string> {
    const block: Record<string, string> = {};
    BRAND_COLOR_TOKENS.forEach((t) => {
      block[t.token] = this.ensureHex6(
        val?.[colorControlName(t.token, dark)] ?? existing?.[t.token],
        dark ? t.dark : t.light
      );
    });
    return block;
  }


  loadData(): void {
    this.isLoading = true;
    this.brandingService.getBrands('', true).subscribe({
      next: (res: any) => {
        this.isLoading = false;
        const brandList = Array.isArray(res)
          ? res
          : (res?.items || res?.data?.items || res?.data || res?.result || []);

        this.brands = brandList.map((item: any) => {
          let brandDoc = item.brand;
          if (typeof brandDoc === 'string') {
            try {
              brandDoc = JSON.parse(brandDoc);
            } catch (e) {
              brandDoc = {};
            }
          }
          brandDoc = brandDoc || {};

          const itemLogo = item.logo !== undefined ? item.logo : (brandDoc?.assets?.logo ?? null);
          const itemLogoDark = item.logo_dark !== undefined ? item.logo_dark : (brandDoc?.assets?.logo_dark ?? null);
          const itemIcon = item.icon !== undefined ? item.icon : (brandDoc?.assets?.icon ?? null);

          return {
            ...item,
            name: item.name || 'Brand',
            display_name: item.display_name || brandDoc?.identity?.display_name || '',
            description: item.description || brandDoc?.meta?.description || brandDoc?.identity?.description || '',
            primary: this.ensureHex6(item.primary || brandDoc?.colors?.primary, '#FCBA03'),
            secondary: this.ensureHex6(item.secondary || brandDoc?.colors?.secondary, '#122331'),
            logo: itemLogo,
            logo_dark: itemLogoDark,
            icon: itemIcon,
            brand: {
              ...brandDoc,
              assets: {
                ...(brandDoc?.assets || {}),
                logo: itemLogo,
                logo_dark: itemLogoDark,
                icon: itemIcon,
              }
            }
          };
        });

        // Auto-select first brand for domain form if not set
        if (this.brands.length > 0 && !this.domainForm.get('branding_id')?.value) {
          const defaultBrand = this.brands.find((b) => b.is_default) || this.brands[0];
          this.domainForm.patchValue({ branding_id: defaultBrand.id });
        }
        this.cdRef.detectChanges();
      },
      error: (err) => {
        this.isLoading = false;
        console.error('Failed to load brands:', err);
        this.sharedService.showError(err?.error?.message || 'Failed to load brands');
      },
    });

    this.loadDomains();
  }

  loadDomains(): void {
    this.brandingService.getDomains().subscribe({
      next: (res: any) => {
        this.domains = Array.isArray(res)
          ? res
          : (res?.items || res?.data?.items || res?.data || res?.result || []);
        this.cdRef.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load domains:', err);
        this.sharedService.showError(err?.error?.message || 'Failed to load domains');
      },
    });
  }

  // --- BRAND ACTIONS ---
  openCreateBrandModal(): void {
    this.editingBrand = null;
    this.selectedFiles = {};
    this.uploadedAssetObjects = {};
    this.originalBrandAssets = {};
    this.brandForm.reset({
      name: '',
      description: '',
      is_default: false,
      is_active: true,
      ...this.defaultColorValues(),
      font_source: 'google',
      font_family: 'Inter',
      font_family_ar: 'Vazirmatn',
      google_family: 'Inter:wght@300;400;500;600;700;800',
      base_size: 16,
      radius: 12,
      button_radius: 12,
      shell_max: 400,
      default_theme: 'system',
      default_language: 'en',
      display_name: '',
      display_name_ar: '',
      tagline: '',
      tagline_ar: '',
      title: '',
      title_ar: '',
      meta_description: '',
      meta_description_ar: '',
      logo_url: '',
      logo_dark_url: '',
      icon_url: '',
      support_email: '',
      support_phone: '',
      support_whatsapp: '',
      support_website: '',
      social_instagram: '',
      social_x: '',
      social_youtube: '',
      social_tiktok: '',
      social_snapchat: '',
      social_linkedin: '',
      powered_by: true,
      terms_url: '',
      privacy_url: '',
    });
    this.isBrandModalVisible = true;
  }

  ensureHex6(color: any, fallback: string = '#FCBA03'): string {
    if (!color || typeof color !== 'string') return fallback;
    let c = color.trim();
    if (!c) return fallback;
    if (!c.startsWith('#')) c = '#' + c;
    if (/^#[0-9a-fA-F]{3}$/.test(c)) {
      c = '#' + c[1] + c[1] + c[2] + c[2] + c[3] + c[3];
    }
    if (/^#[0-9a-fA-F]{8}$/.test(c)) {
      c = c.substring(0, 7);
    }
    if (/^#[0-9a-fA-F]{6}$/.test(c)) {
      return c.toUpperCase();
    }
    return fallback;
  }

  getBrandThemeColor(brand: any, type: 'primary' | 'secondary' | 'accent' | 'heading' | 'bg' | 'surface'): string {
    const fallbacks: Record<string, string> = {
      primary: '#FCBA03',
      secondary: '#122331',
      accent: '#FCBA03',
      heading: '#122331',
      bg: '#F7F6F9',
      surface: '#FFFFFF',
    };
    if (!brand) return fallbacks[type] || '#FCBA03';
    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};
    const colors = b.colors || brand.colors || {};
    switch (type) {
      case 'primary':
        return this.ensureHex6(brand.primary || brand.primary_color || colors.primary, fallbacks['primary']);
      case 'secondary':
        return this.ensureHex6(brand.secondary || brand.secondary_color || colors.secondary, fallbacks['secondary']);
      case 'accent':
        return this.ensureHex6(brand.accent || brand.accent_color || colors.accent || colors.primary || brand.primary, fallbacks['accent']);
      case 'heading':
        return this.ensureHex6(brand.heading || brand.heading_color || colors.heading || colors.text, fallbacks['heading']);
      case 'bg':
        return this.ensureHex6(brand.bg || brand.bg_color || colors.bg || colors.background, fallbacks['bg']);
      case 'surface':
        return this.ensureHex6(brand.surface || brand.surface_color || colors.surface || colors.card || colors.sheet, fallbacks['surface']);
      default:
        return fallbacks[type] || '#FCBA03';
    }
  }

  onColorHexInput(controlName: string, event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value?.trim() || '';
    if (val && !val.startsWith('#') && /^[0-9a-fA-F]{1,6}$/.test(val)) {
      val = '#' + val;
    }
    if (/^#[0-9a-fA-F]{6}$/i.test(val)) {
      this.brandForm.get(controlName)?.setValue(val.toUpperCase(), { emitEvent: true });
    }
  }

  onColorHexBlur(controlName: string): void {
    const current = this.brandForm.get(controlName)?.value;
    const fixed = this.ensureHex6(current, '#FCBA03');
    this.brandForm.get(controlName)?.setValue(fixed, { emitEvent: true });
  }

  getBrandAvatarLogo(brand: any): string {
    if (!brand) return '';
    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    return (
      this.extractUrl(brand.logo) ||
      this.extractUrl(brand.logo_dark) ||
      this.extractUrl(b.assets?.logo) ||
      this.extractUrl(b.assets?.logo_dark) ||
      this.extractUrl(brand.icon) ||
      this.extractUrl(b.assets?.icon) ||
      ''
    );
  }

  extractUrl(asset: any): string {
    if (!asset) return '';

    // Handle Array
    if (Array.isArray(asset) && asset.length > 0) {
      return this.extractUrl(asset[0]);
    }

    // Handle Object
    if (typeof asset === 'object' && asset !== null) {
      if (asset.url && typeof asset.url === 'string') return this.extractUrl(asset.url);
      if (asset.path && typeof asset.path === 'string') return this.extractUrl(asset.path);
      return '';
    }

    // Handle String
    if (typeof asset === 'string') {
      let trimmed = asset.trim();
      if (!trimmed) return '';

      // If it's a JSON stringified array or object
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed) return this.extractUrl(parsed);
        } catch (e) {
          // ignore
        }
      }

      // If multiple http(s):// are concatenated (e.g. https://domainhttps://domain/vault/...)
      const lastHttpIdx = trimmed.lastIndexOf('http://');
      const lastHttpsIdx = trimmed.lastIndexOf('https://');
      const lastIdx = Math.max(lastHttpIdx, lastHttpsIdx);
      if (lastIdx > 0) {
        trimmed = trimmed.substring(lastIdx);
      }

      // If backend returned relative /vault/ path directly
      if (trimmed.startsWith('/vault/')) {
        return 'https://xi5f-5y62-kbyr.n7e.xano.io' + trimmed;
      }

      return trimmed;
    }

    return '';
  }

  cleanAssetForPayload(asset: any): any {
    if (!asset) return null;

    if (Array.isArray(asset)) {
      if (asset.length === 0) return null;
      return this.cleanAssetForPayload(asset[0]);
    }

    if (typeof asset === 'string') {
      const trimmed = asset.trim();
      if (!trimmed) return null;
      let path = trimmed;
      const vaultIdx = trimmed.lastIndexOf('/vault/');
      if (vaultIdx !== -1) {
        path = trimmed.substring(vaultIdx);
      }
      const filename = path.split('/').pop() || 'image.png';
      return {
        access: 'public',
        path: path,
        name: filename,
        type: 'image',
      };
    }

    if (typeof asset === 'object' && asset !== null) {
      const obj = { ...asset };
      if (obj.path && typeof obj.path === 'string') {
        const vaultIdx = obj.path.lastIndexOf('/vault/');
        if (vaultIdx !== -1) {
          obj.path = obj.path.substring(vaultIdx);
        }
      } else if (obj.url && typeof obj.url === 'string') {
        const vaultIdx = obj.url.lastIndexOf('/vault/');
        if (vaultIdx !== -1) {
          obj.path = obj.url.substring(vaultIdx);
        }
      }
      if (obj.url && typeof obj.url === 'string') {
        const vaultIdx = obj.url.lastIndexOf('/vault/');
        if (vaultIdx !== -1) {
          obj.url = 'https://xi5f-5y62-kbyr.n7e.xano.io' + obj.url.substring(vaultIdx);
        }
      }
      obj.access = obj.access || 'public';
      obj.type = obj.type || 'image';
      return obj;
    }

    return null;
  }

  openEditBrandModal(brand: BrandItem): void {
    this.editingBrand = brand;
    this.selectedFiles = {};
    this.uploadedAssetObjects = {};

    let b = brand.brand;
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    // Keep exact original backend assets
    this.originalBrandAssets = {
      logo_url: brand.logo !== undefined ? brand.logo : (b.assets?.logo ?? null),
      logo_dark_url: brand.logo_dark !== undefined ? brand.logo_dark : (b.assets?.logo_dark ?? null),
      icon_url: brand.icon !== undefined ? brand.icon : (b.assets?.icon ?? null),
    };

    const colors = b.colors || {};
    const colorsDark = b.colors_dark || colors.dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};
    const legal = b.legal || {};
    const defaults = b.defaults || {};

    const logoUrl = this.extractUrl(brand.logo) || this.extractUrl(b.assets?.logo);
    const logoDarkUrl = this.extractUrl(brand.logo_dark) || this.extractUrl(b.assets?.logo_dark);
    const iconUrl = this.extractUrl(brand.icon) || this.extractUrl(b.assets?.icon);

    const desc = brand.description || b.meta?.description || b.identity?.description || b.description || '';

    this.brandForm.patchValue({
      name: brand.name || idn.display_name || '',
      description: desc,
      is_default: brand.is_default || false,
      is_active: brand.is_active ?? true,
      ...this.colorValuesFromBrand(colors, colorsDark, brand),
      font_source: typo.source || 'google',
      font_family: typo.family || 'Inter',
      font_family_ar: typo.family_ar || 'Vazirmatn',
      google_family: typo.google_family || 'Inter:wght@300;400;500;600;700;800',
      base_size: Number(typo.base_size ?? 16),
      radius: shape.radius ?? 12,
      button_radius: shape.button_radius ?? 12,
      shell_max: shape.shell_max ?? 400,
      default_theme: defaults.theme || 'system',
      default_language: defaults.language || 'en',
      display_name: idn.display_name || brand.name || '',
      display_name_ar: idn.display_name_ar || '',
      tagline: idn.tagline || '',
      tagline_ar: idn.tagline_ar || '',
      title: meta.title || '',
      title_ar: meta.title_ar || '',
      meta_description: meta.description || desc,
      meta_description_ar: meta.description_ar || '',
      logo_url: logoUrl,
      logo_dark_url: logoDarkUrl,
      icon_url: iconUrl,
      support_email: support.email || '',
      support_phone: support.phone || '',
      support_whatsapp: support.whatsapp || '',
      support_website: support.website || '',
      social_instagram: social.instagram || '',
      social_x: social.x || '',
      social_youtube: social.youtube || '',
      social_tiktok: social.tiktok || '',
      social_snapchat: social.snapchat || '',
      social_linkedin: social.linkedin || '',
      powered_by: b.flags?.powered_by ?? true,
      terms_url: legal.terms_url || '',
      privacy_url: legal.privacy_url || '',
    });
    this.isBrandModalVisible = true;
  }

  /* ----------------------------- Live preview helpers ----------------------------- */

  /** Picks the light or dark form value depending on the preview tab. */
  pv(lightControl: string, darkControl: string, fallbackLight: string, fallbackDark: string): string {
    const v = this.brandForm?.value || {};
    return this.colorThemeTab === 'dark'
      ? (v[darkControl] || fallbackDark)
      : (v[lightControl] || fallbackLight);
  }

  get previewIsArabic(): boolean {
    return this.brandForm?.value?.default_language === 'ar';
  }

  get previewDir(): 'rtl' | 'ltr' {
    return this.previewIsArabic ? 'rtl' : 'ltr';
  }

  get previewFontFamily(): string {
    const v = this.brandForm?.value || {};
    const family = this.previewIsArabic
      ? (v.font_family_ar || 'Vazirmatn')
      : (v.font_family || 'Inter');
    return `'${family}', sans-serif`;
  }

  /** Preview shell is ~55% of the real width, so scale the base size the same way. */
  get previewFontSize(): number {
    const base = Number(this.brandForm?.value?.base_size ?? 16);
    if (!base || isNaN(base)) return 9;
    return Math.round(base * 0.6 * 10) / 10;
  }

  /** Inner width of the mock phone screen, in px. */
  readonly previewScreenWidth = 244;

  /** shell_max scaled into the phone screen, never wider than the screen itself. */
  get previewShellWidth(): number {
    const shell = Number(this.brandForm?.value?.shell_max ?? 400);
    if (!shell || isNaN(shell)) return this.previewScreenWidth;
    return Math.min(this.previewScreenWidth, Math.max(140, Math.round(shell * 0.61)));
  }

  get previewName(): string {
    const v = this.brandForm?.value || {};
    return (this.previewIsArabic ? v.display_name_ar : v.display_name) || v.display_name || v.name || 'Your Brand';
  }

  get previewTagline(): string {
    const v = this.brandForm?.value || {};
    return (this.previewIsArabic ? v.tagline_ar : v.tagline) || v.tagline || 'Scan, Play, Win';
  }

  get previewTitle(): string {
    const v = this.brandForm?.value || {};
    return (this.previewIsArabic ? v.title_ar : v.title) || v.title || `Join the contest | ${this.previewName}`;
  }

  get previewSocials(): { icon: string; url: string }[] {
    const v = this.brandForm?.value || {};
    return [
      { icon: 'fa-brands fa-x-twitter', url: v.social_x },
      { icon: 'fa-brands fa-instagram', url: v.social_instagram },
      { icon: 'fa-brands fa-youtube', url: v.social_youtube },
      { icon: 'fa-brands fa-tiktok', url: v.social_tiktok },
      { icon: 'fa-brands fa-snapchat', url: v.social_snapchat },
      { icon: 'fa-brands fa-linkedin', url: v.social_linkedin },
    ].filter((s) => !!s.url);
  }

  get previewLogoUrl(): string {
    const v = this.brandForm?.value || {};
    const raw = this.colorThemeTab === 'dark' ? (v.logo_dark_url || v.logo_url) : v.logo_url;
    return this.extractUrl(raw);
  }

  /** Optional text fields go to the backend as null (not '') when the user leaves them empty. */
  private orNull(value: any): string | null {
    if (value === undefined || value === null) return null;
    const trimmed = String(value).trim();
    return trimmed.length ? trimmed : null;
  }

  buildCompleteBrandDocument(val: any, existingBrand?: any): any {
    let b = existingBrand?.brand || {};
    if (typeof b === 'string') {
      try {
        b = JSON.parse(b);
      } catch (e) {
        b = {};
      }
    }
    b = b || {};

    const colors = b.colors || {};
    const colorsDark = b.colors_dark || colors.dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};
    const legal = b.legal || {};
    const flags = b.flags || {};
    const defaults = b.defaults || {};

    // Every colour comes straight from the form, falling back to the saved brand and
    // then the schema default. card/sheet used to be forced equal to surface, which made
    // three separate tokens impossible to set apart.
    const lightColors = this.buildColorsBlock(val, colors, false);
    const darkColorsMap = this.buildColorsBlock(val, { ...colors, ...colorsDark }, true);

    const displayName = val?.display_name || idn.display_name || val?.name || existingBrand?.name || 'Brand';
    const displayNameAr = val?.display_name_ar || idn.display_name_ar || '';
    const desc = val?.description || val?.meta_description || meta.description || idn.description || existingBrand?.description || (displayName ? `Join ${displayName} contests straight from your browser. Scan a QR code, play and win.` : '');
    const descAr = val?.meta_description_ar || meta.description_ar || idn.description_ar || (displayNameAr ? `انضم إلى حملات ${displayNameAr} مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح.` : 'مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح انضم إلى حملات');

    const logoAsset = (this.uploadedAssetObjects?.['logo_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['logo_url']) : null) || existingBrand?.logo || b.assets?.logo || (val?.logo_url ? this.cleanAssetForPayload(val.logo_url) : null);
    const logoDarkAsset = (this.uploadedAssetObjects?.['logo_dark_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['logo_dark_url']) : null) || existingBrand?.logo_dark || b.assets?.logo_dark || (val?.logo_dark_url ? this.cleanAssetForPayload(val.logo_dark_url) : null);
    const iconAsset = (this.uploadedAssetObjects?.['icon_url'] ? this.cleanAssetForPayload(this.uploadedAssetObjects['icon_url']) : null) || existingBrand?.icon || b.assets?.icon || (val?.icon_url ? this.cleanAssetForPayload(val.icon_url) : null);
    const ogImageAsset = existingBrand?.og_image || b.assets?.og_image || null;

    return {
      meta: {
        title: val?.title || meta.title || (displayName ? `Join the campaign | ${displayName}` : ''),
        title_ar: val?.title_ar || meta.title_ar || (displayNameAr ? `انضم إلى الحملة | ${displayNameAr}` : ''),
        description: desc,
        description_ar: descAr,
      },
      flags: {
        powered_by: val?.powered_by !== undefined ? !!val.powered_by : (flags.powered_by !== undefined ? !!flags.powered_by : true),
      },
      legal: {
        terms_url: this.orNull(val?.terms_url ?? legal.terms_url),
        privacy_url: this.orNull(val?.privacy_url ?? legal.privacy_url),
      },
      shape: {
        radius: Number(val?.radius ?? shape.radius ?? 12),
        radius_lg: Number(val?.radius ? Number(val.radius) * 2 : (shape.radius_lg ?? 24)),
        button_radius: Number(val?.button_radius ?? shape.button_radius ?? 12),
        shell_max: Number(val?.shell_max ?? shape.shell_max ?? 400),
      },
      colors: lightColors,
      colors_dark: darkColorsMap,
      social: {
        x: this.orNull(val?.social_x ?? social.x),
        instagram: this.orNull(val?.social_instagram ?? social.instagram),
        tiktok: this.orNull(val?.social_tiktok ?? social.tiktok),
        snapchat: this.orNull(val?.social_snapchat ?? social.snapchat),
        youtube: this.orNull(val?.social_youtube ?? social.youtube),
        linkedin: this.orNull(val?.social_linkedin ?? social.linkedin),
      },
      support: {
        email: this.orNull(val?.support_email ?? support.email),
        phone: this.orNull(val?.support_phone ?? support.phone),
        whatsapp: this.orNull(val?.support_whatsapp ?? support.whatsapp),
        website: this.orNull(val?.support_website ?? support.website),
      },
      identity: {
        display_name: displayName,
        display_name_ar: displayNameAr,
        tagline: val?.tagline || idn.tagline || '',
        tagline_ar: val?.tagline_ar || idn.tagline_ar || '',
        description: desc,
        description_ar: descAr,
      },
      typography: {
        source: val?.font_source || typo.source || 'google',
        family: val?.font_family || typo.family || 'Inter',
        family_ar: val?.font_family_ar || typo.family_ar || 'Vazirmatn',
        heading_family: typo.heading_family || val?.font_family || 'Inter',
        google_family: val?.font_source === 'google' ? (val?.google_family || typo.google_family || `${val?.font_family || 'Inter'}:wght@300;400;500;600;700;800`) : 'Inter:wght@300;400;500;600;700;800',
        google_family_ar: `${val?.font_family_ar || typo.family_ar || 'Vazirmatn'}:wght@300;400;500;600;700;800`,
        fallback: typo.fallback || 'sans-serif',
        base_size: Number(val?.base_size ?? typo.base_size ?? 16),
      },
      defaults: {
        theme: val?.default_theme || defaults.theme || 'system',
        language: val?.default_language || defaults.language || 'en',
      },
      assets: {
        logo: logoAsset,
        logo_dark: logoDarkAsset,
        icon: iconAsset,
        og_image: ogImageAsset,
      },
    };
  }

  buildFullBrandJson(val: any, existingBrand?: any): any {
    const brandDoc = this.buildCompleteBrandDocument(val, existingBrand);
    const name = val?.name || existingBrand?.name || brandDoc.identity?.display_name || 'Brand';
    const desc = val?.description !== undefined ? val.description : (existingBrand?.description !== undefined ? existingBrand.description : (brandDoc.meta?.description || ''));
    const isActive = val?.is_active !== undefined ? !!val.is_active : (existingBrand?.is_active !== undefined ? !!existingBrand.is_active : true);
    const isDefault = val?.is_default !== undefined ? !!val.is_default : (existingBrand?.is_default !== undefined ? !!existingBrand.is_default : false);

    return {
      name: name,
      description: desc,
      is_active: isActive,
      is_default: isDefault,
      brand: brandDoc,
    };
  }

  saveBrand(): void {
    if (this.brandForm.invalid) {
      const invalidFields: string[] = [];
      Object.keys(this.brandForm.controls).forEach((key) => {
        const ctrl = this.brandForm.get(key);
        if (ctrl?.invalid) {
          ctrl.markAsTouched();
          invalidFields.push(key);
        }
      });
      this.sharedService.showError(`Please fix invalid fields: ${invalidFields.join(', ')}`);
      return;
    }

    const val = this.brandForm.value;

    const prepareAssetForPayload = (field: 'logo_url' | 'logo_dark_url' | 'icon_url') => {
      const formVal = this.brandForm.get(field)?.value;
      if (!formVal) return null;

      // 1. If a new file was uploaded by user in this modal session -> send clean baseToImage response
      if (this.uploadedAssetObjects[field]) {
        return this.cleanAssetForPayload(this.uploadedAssetObjects[field]);
      }

      // 2. If editing existing brand and no new file was chosen -> send clean original backend asset
      if (this.editingBrand) {
        const orig = this.originalBrandAssets[field];
        if (orig !== undefined && orig !== null && orig !== '') {
          return this.cleanAssetForPayload(orig);
        }
      }

      return null;
    };

    const logoPayload = prepareAssetForPayload('logo_url');
    const logoDarkPayload = prepareAssetForPayload('logo_dark_url');
    const iconPayload = prepareAssetForPayload('icon_url');

    // Assemble the complete Brand document
    const brandDoc = this.buildCompleteBrandDocument(val, this.editingBrand);

    this.isSavingBrand = true;

    const parseErrorMessage = (err: any, fallback: string): string => {
      if (!err) return fallback;
      console.error(fallback, err);
      if (err.error) {
        if (typeof err.error === 'string') return err.error;
        if (err.error.message && typeof err.error.message === 'string') return err.error.message;
        if (err.error.error) return typeof err.error.error === 'string' ? err.error.error : JSON.stringify(err.error.error);
        if (err.error.detail) return typeof err.error.detail === 'string' ? err.error.detail : JSON.stringify(err.error.detail);
        if (err.error.errors) return typeof err.error.errors === 'string' ? err.error.errors : JSON.stringify(err.error.errors);
        try {
          return JSON.stringify(err.error);
        } catch (e) {
          // ignore stringify error
        }
      }
      return err.message || fallback;
    };

    if (this.editingBrand) {
      const targetId = Number(this.editingBrand.id || (this.editingBrand as any)._id || (this.editingBrand as any).branding_id);
      const payload: any = {
        id: targetId,
        name: val.name || 'Brand',
        brand: brandDoc,
        is_active: val.is_active ?? true,
        logo: logoPayload,
        logo_dark: logoDarkPayload,
        icon: iconPayload,
      };

      this.brandingService.updateBrand(payload).subscribe({
        next: (res: any) => {
          this.isSavingBrand = false;
          this.isBrandModalVisible = false;
          this.sharedService.showSuccess('Brand updated successfully');

          // Immediately mutate local state and trigger CD for instant UI refresh
          const responseBrand = res?.brand || res?.data?.brand || {};
          const mergedBrandDoc = {
            ...brandDoc,
            ...responseBrand,
            assets: {
              logo: logoPayload !== undefined ? logoPayload : (responseBrand.assets?.logo ?? brandDoc.assets?.logo ?? null),
              logo_dark: logoDarkPayload !== undefined ? logoDarkPayload : (responseBrand.assets?.logo_dark ?? brandDoc.assets?.logo_dark ?? null),
              icon: iconPayload !== undefined ? iconPayload : (responseBrand.assets?.icon ?? brandDoc.assets?.icon ?? null),
              og_image: responseBrand.assets?.og_image ?? brandDoc.assets?.og_image ?? null,
            },
          };

          if (this.editingBrand) {
            const idx = this.brands.findIndex((b) => b.id === targetId || (b as any)._id === targetId);
            if (idx !== -1) {
              this.brands[idx] = {
                ...this.brands[idx],
                name: val.name || this.brands[idx].name,
                description: val.description,
                primary: brandDoc.colors.primary,
                secondary: brandDoc.colors.secondary,
                brand: mergedBrandDoc,
                is_active: val.is_active ?? this.brands[idx].is_active,
                logo: logoPayload !== undefined ? logoPayload : (res?.logo ?? null),
                logo_dark: logoDarkPayload !== undefined ? logoDarkPayload : (res?.logo_dark ?? null),
                icon: iconPayload !== undefined ? iconPayload : (res?.icon ?? null),
              };
              this.brands = [...this.brands];
              this.cdRef.detectChanges();
            }
          }
          this.loadData();
        },
        error: (err) => {
          this.isSavingBrand = false;
          const errMsg = parseErrorMessage(err, 'Failed to update brand');
          this.sharedService.showError(errMsg);
        },
      });
    } else {
      const payload: any = {
        name: val.name || 'Brand',
        brand: brandDoc,
        is_default: val.is_default || false,
        is_active: true,
        logo: logoPayload,
        logo_dark: logoDarkPayload,
        icon: iconPayload,
      };

      this.brandingService.createBrand(payload).subscribe({
        next: (res: any) => {
          this.isSavingBrand = false;
          this.isBrandModalVisible = false;
          this.sharedService.showSuccess('Brand created successfully');

          const responseBrand = res?.brand || res?.data?.brand || {};
          const mergedBrandDoc = {
            ...brandDoc,
            ...responseBrand,
            assets: {
              logo: logoPayload ?? responseBrand.assets?.logo ?? brandDoc.assets?.logo ?? null,
              logo_dark: logoDarkPayload ?? responseBrand.assets?.logo_dark ?? brandDoc.assets?.logo_dark ?? null,
              icon: iconPayload ?? responseBrand.assets?.icon ?? brandDoc.assets?.icon ?? null,
              og_image: responseBrand.assets?.og_image ?? brandDoc.assets?.og_image ?? null,
            },
          };

          const newBrandItem: BrandItem = {
            id: res?.id || res?.data?.id || Date.now(),
            name: val.name || 'Brand',
            description: val.description || '',
            primary: brandDoc.colors.primary,
            secondary: brandDoc.colors.secondary,
            is_default: val.is_default || false,
            is_active: true,
            brand: mergedBrandDoc,
            logo: logoPayload || (res?.logo || null),
            logo_dark: logoDarkPayload || (res?.logo_dark || null),
            icon: iconPayload || (res?.icon || null),
          };
          this.brands = [newBrandItem, ...this.brands];
          this.cdRef.detectChanges();
          this.loadData();
        },
        error: (err) => {
          this.isSavingBrand = false;
          const errMsg = parseErrorMessage(err, 'Failed to create brand');
          this.sharedService.showError(errMsg);
        },
      });
    }
  }

  setDefaultBrand(brand: BrandItem): void {
    if (brand.is_default) return;
    this.brandingService.setDefaultBrand(brand.id).subscribe({
      next: () => {
        this.sharedService.showSuccess(`'${brand.name}' is now default brand`);
        this.loadData();
      },
      error: (err) => {
        this.sharedService.showError(err?.error?.message || 'Failed to set default brand');
      },
    });
  }

  toggleBrandActive(brand: BrandItem): void {
    const newActiveState = !brand.is_active;
    this.brandingService.updateBrand({ id: brand.id, is_active: newActiveState }).subscribe({
      next: () => {
        this.sharedService.showSuccess(`Brand ${newActiveState ? 'activated' : 'deactivated'}`);
        brand.is_active = newActiveState;
      },
      error: (err) => {
        this.sharedService.showError(err?.error?.message || 'Failed to toggle brand status');
      },
    });
  }

  // --- DOMAIN ACTIONS ---
  openCreateDomainModal(brandId?: number): void {
    this.editingDomain = null;
    const selectedBrandId = brandId ? Number(brandId) : null;
    this.domainForm.reset({
      hostname: '',
      branding_id: selectedBrandId,
      is_primary: false,
      notes: '',
    });
    this.isDomainModalVisible = true;
  }

  saveDomain(): void {
    if (this.domainForm.invalid) {
      Object.values(this.domainForm.controls).forEach((ctrl) => ctrl.markAsTouched());
      return;
    }

    const val = this.domainForm.value;
    this.isSavingDomain = true;

    this.brandingService.createDomain(val).subscribe({
      next: () => {
        this.isSavingDomain = false;
        this.isDomainModalVisible = false;
        this.sharedService.showSuccess('Domain added successfully');
        this.loadDomains();
      },
      error: (err) => {
        this.isSavingDomain = false;
        this.sharedService.showError(err?.error?.message || 'Failed to add domain');
      },
    });
  }

  verifyDomain(domain: DomainItem): void {
    this.verifyingDomainId = domain.id;
    this.brandingService.verifyDomain(domain.id).subscribe({
      next: (res: any) => {
        this.verifyingDomainId = null;
        if (res?.status === 'live' || res?.is_verified) {
          this.sharedService.showSuccess('Domain DNS verified! Status is now LIVE.');
        } else {
          this.sharedService.showSuccess(res?.message || 'Verification check complete. DNS propagation may take up to an hour.');
        }
        this.loadDomains();
      },
      error: (err) => {
        this.verifyingDomainId = null;
        this.sharedService.showError(err?.error?.message || 'DNS Verification check failed');
      },
    });
  }

  deleteDomain(domain: DomainItem): void {
    if (confirm(`Are you sure you want to remove domain '${domain.hostname}'?`)) {
      this.brandingService.deleteDomain(domain.id).subscribe({
        next: () => {
          this.sharedService.showSuccess('Domain deleted');
          this.loadDomains();
        },
        error: (err) => {
          this.sharedService.showError(err?.error?.message || 'Failed to delete domain');
        },
      });
    }
  }

  showDnsSetup(domain: DomainItem): void {
    this.selectedDnsSetup = {
      hostname: domain.hostname,
      dns: domain.dns_setup,
    };
    this.dnsModalVisible = true;
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text).then(() => {
      this.sharedService.showSuccess('Copied to clipboard');
    });
  }

  getBrandName(brandingId: number): string {
    const b = this.brands.find((x) => x.id === brandingId);
    return b ? (b.display_name || b.name) : `Brand #${brandingId}`;
  }

  getBrandDomains(brandId: number): any[] {
    const brand = this.brands.find((b) => b.id === brandId);
    if (brand && Array.isArray((brand as any).domains) && (brand as any).domains.length > 0) {
      return (brand as any).domains;
    }
    if (!this.domains || !Array.isArray(this.domains)) return [];
    return this.domains.filter((d) => d.branding_id === brandId);
  }

  hasBrandDomains(brandId: number): boolean {
    return this.getBrandDomains(brandId).length > 0;
  }

  // --- JSON EDIT & UPLOAD ACTIONS (INSIDE BRAND FORM & DIRECT) ---
  applyJsonToForm(parsed: any): void {
    if (!parsed || typeof parsed !== 'object') return;

    let b = parsed.brand || {};
    if (!parsed.brand && (parsed.colors || parsed.typography || parsed.shape || parsed.identity || parsed.meta)) {
      b = parsed;
    }

    const colors = b.colors || {};
    const colorsDark = b.colors?.dark || b.colors_dark || {};
    const typo = b.typography || {};
    const shape = b.shape || {};
    const idn = b.identity || {};
    const meta = b.meta || {};
    const support = b.support || {};
    const social = b.social || {};

    const patch: any = {};
    if (parsed.name) patch.name = parsed.name;
    else if (idn.display_name) patch.name = idn.display_name;

    const resolvedDesc = parsed.description !== undefined
      ? parsed.description
      : (b.meta?.description !== undefined
        ? b.meta.description
        : (b.identity?.description !== undefined
          ? b.identity.description
          : (b.description !== undefined ? b.description : undefined)));

    if (resolvedDesc !== undefined) {
      patch.description = resolvedDesc;
      patch.meta_description = resolvedDesc;
    }

    if (parsed.is_active !== undefined) patch.is_active = parsed.is_active;
    if (parsed.is_default !== undefined) patch.is_default = parsed.is_default;

    Object.assign(patch, this.colorPatchFromJson(colors, colorsDark));

    // Typography
    if (typo.source) patch.font_source = typo.source;
    if (typo.family) patch.font_family = typo.family;
    if (typo.family_ar) patch.font_family_ar = typo.family_ar;
    if (typo.google_family) patch.google_family = typo.google_family;
    if (typo.base_size !== undefined) patch.base_size = Number(typo.base_size);

    // Defaults
    const defaults = b.defaults || {};
    if (defaults.theme) patch.default_theme = defaults.theme;
    if (defaults.language) patch.default_language = defaults.language;

    // Shape
    if (shape.radius !== undefined) patch.radius = Number(shape.radius);
    if (shape.button_radius !== undefined) patch.button_radius = Number(shape.button_radius);
    if (shape.shell_max !== undefined) patch.shell_max = Number(shape.shell_max);

    // Identity & Meta
    if (idn.display_name !== undefined) patch.display_name = idn.display_name;
    if (idn.display_name_ar !== undefined) patch.display_name_ar = idn.display_name_ar;
    if (idn.tagline !== undefined) patch.tagline = idn.tagline;
    if (idn.tagline_ar !== undefined) patch.tagline_ar = idn.tagline_ar;
    if (meta.title !== undefined) patch.title = meta.title;
    if (meta.title_ar !== undefined) patch.title_ar = meta.title_ar;
    if (meta.description !== undefined) patch.meta_description = meta.description;
    if (meta.description_ar !== undefined) patch.meta_description_ar = meta.description_ar;

    // Support & Social
    if (support.email) patch.support_email = support.email;
    if (support.phone) patch.support_phone = support.phone;
    if (support.whatsapp) patch.support_whatsapp = support.whatsapp;
    if (support.website) patch.support_website = support.website;
    if (social.instagram) patch.social_instagram = social.instagram;
    if (social.x) patch.social_x = social.x;
    if (social.youtube) patch.social_youtube = social.youtube;
    if (social.tiktok) patch.social_tiktok = social.tiktok;
    if (social.snapchat) patch.social_snapchat = social.snapchat;
    if (social.linkedin) patch.social_linkedin = social.linkedin;
    if (b.flags?.powered_by !== undefined) patch.powered_by = b.flags.powered_by;

    // Legal
    const legal = b.legal || {};
    if (legal.terms_url) patch.terms_url = legal.terms_url;
    if (legal.privacy_url) patch.privacy_url = legal.privacy_url;

    // Assets - Only overwrite form logo if new non-empty URL is provided in JSON
    const logoUrl = this.extractUrl(parsed.logo) || this.extractUrl(b.assets?.logo);
    const logoDarkUrl = this.extractUrl(parsed.logo_dark) || this.extractUrl(b.assets?.logo_dark);
    const iconUrl = this.extractUrl(parsed.icon) || this.extractUrl(b.assets?.icon);

    if (logoUrl && typeof logoUrl === 'string' && logoUrl.trim().length > 0) patch.logo_url = logoUrl;
    if (logoDarkUrl && typeof logoDarkUrl === 'string' && logoDarkUrl.trim().length > 0) patch.logo_dark_url = logoDarkUrl;
    if (iconUrl && typeof iconUrl === 'string' && iconUrl.trim().length > 0) patch.icon_url = iconUrl;

    this.brandForm.patchValue(patch);
    this.brandForm.markAsDirty();
    this.cdRef.detectChanges();
  }

  openEditJsonFromForm(): void {
    const val = this.brandForm.value;
    const fullJson = this.buildFullBrandJson(val, this.editingBrand);

    this.jsonModalBrand = this.editingBrand || ({ id: 0, name: val.name || 'New Brand' } as any);
    this.rawJsonString = JSON.stringify(fullJson, null, 2);
    this.jsonError = '';
    this.isCopied = false;
    this.isJsonModalVisible = true;
  }

  openUploadJsonFromForm(): void {
    this.uploadJsonBrand = this.editingBrand || ({ id: 0, name: this.brandForm.value.name || 'New Brand' } as any);
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  getExampleBrandJson(): any {
    return {
      name: 'DigiContests Brand',
      description: 'Premium gamified campaign and interactive campaign portal.',
      is_active: true,
      is_default: false,
      brand: {
        identity: {
          display_name: 'DigiContests',
          display_name_ar: 'ديجي كونتيستس',
          tagline: 'Scan, Play, Win',
          tagline_ar: 'امسح، العب، واربح',
          description: 'Join DigiContests campaigns straight from your browser. Scan a QR code, play and win.',
          description_ar: 'انضم إلى حملات ديجي كونتيستس مباشرة من متصفحك. امسح رمز الاستجابة السريعة، العب واربح.'
        },
        meta: {
          title: 'DigiContests | Win Amazing Prizes',
          title_ar: 'ديجي كونتيستس | اربح جوائز مذهلة',
          description: 'Engage and participate in exclusive brand campaigns, scan QR codes and win rewards instantly.',
          description_ar: 'شارك في حملات تفاعلية حصرية، امسح رمز الاستجابة السريعة واربح جوائز فورية.'
        },
        colors: {
          primary: '#FCBA03',
          secondary: '#122331',
          accent: '#FCBA03',
          bg: '#F7F6F9',
          card: '#FFFFFF',
          surface: '#FFFFFF',
          surface_alt: '#EBEBEB',
          sheet: '#FFFFFF',
          heading: '#122331',
          text: '#122331',
          text_muted: '#6B7280',
          on_primary: '#1D1D1B',
          card_border: '#EBEBEB',
          card_alt: '#FAFAFA',
          gradient_start: '#FFEAB0',
          gradient_end: '#FFFFFF',
          task: '#FFF6D9',
          success: '#1F9254',
          warning: '#D97706',
          error: '#EF233C',
          completed: '#1F9254',
          disabled: '#F1F1F1',
          grey_1: '#E5E7EB',
          grey_2: '#D1D5DB',
          grey_3: '#9CA3AF',
          grey_4: '#6B7280',
          grey_5: '#3B475C',
          grey_6: '#122331'
        },
        colors_dark: {
          primary: '#FCBA03',
          secondary: '#FFD451',
          accent: '#FCBA03',
          bg: '#0D1620',
          card: '#152331',
          surface: '#152331',
          surface_alt: '#1E3040',
          sheet: '#152331',
          heading: '#FFFFFF',
          text: '#FFFFFF',
          text_muted: '#9CA3AF',
          on_primary: '#1D1D1B',
          card_border: '#24384A',
          card_alt: '#101C27',
          gradient_start: '#4A3505',
          gradient_end: '#0D1620',
          task: '#3A2B04',
          success: '#34D399',
          warning: '#F59E0B',
          error: '#FF5A6E',
          completed: '#34D399',
          disabled: '#22303F',
          grey_1: '#22303F',
          grey_2: '#3C4B5C',
          grey_3: '#6B7A8C',
          grey_4: '#9CA3AF',
          grey_5: '#C0C4CB',
          grey_6: '#E5E7EB'
        },
        typography: {
          source: 'google',
          family: 'Inter',
          family_ar: 'Vazirmatn',
          heading_family: 'Inter',
          google_family: 'Inter:wght@300;400;500;600;700;800',
          google_family_ar: 'Vazirmatn:wght@300;400;500;600;700;800',
          fallback: 'sans-serif',
          base_size: 16
        },
        shape: {
          radius: 12,
          radius_lg: 24,
          button_radius: 12,
          shell_max: 400
        },
        assets: {
          logo: {
            access: 'public',
            path: '/vault/example-logo-light.png',
            name: 'logo-light.png',
            type: 'image'
          },
          logo_dark: {
            access: 'public',
            path: '/vault/example-logo-dark.png',
            name: 'logo-dark.png',
            type: 'image'
          },
          icon: {
            access: 'public',
            path: '/vault/example-favicon.png',
            name: 'favicon.png',
            type: 'image'
          },
          og_image: null
        },
        support: {
          email: 'support@digicontests.com',
          phone: '+966500000000',
          whatsapp: '+966500000000',
          website: 'https://digicontests.com'
        },
        social: {
          x: 'https://x.com/digicontests',
          instagram: 'https://instagram.com/digicontests',
          tiktok: '',
          snapchat: '',
          youtube: '',
          linkedin: ''
        },
        flags: {
          powered_by: true
        },
        legal: {
          terms_url: 'https://digicontests.com/terms',
          privacy_url: 'https://digicontests.com/privacy'
        },
        defaults: {
          theme: 'light',
          language: 'en'
        }
      }
    };
  }

  openExampleJsonModal(): void {
    const example = this.getExampleBrandJson();
    this.rawJsonString = JSON.stringify(example, null, 2);
    this.jsonModalBrand = { id: 0, name: 'Complete Example Schema' } as any;
    this.isExampleJsonModal = true;
    this.jsonError = '';
    this.isCopied = false;
    this.isJsonModalVisible = true;
  }

  openImportJsonModal(): void {
    this.uploadJsonBrand = null;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  openEditJsonModal(brand: BrandItem): void {
    this.isExampleJsonModal = false;
    this.jsonModalBrand = brand;
    this.jsonError = '';
    this.isCopied = false;

    const fullJson = this.buildFullBrandJson(brand, brand);
    this.rawJsonString = JSON.stringify(fullJson, null, 2);
    this.isJsonModalVisible = true;
  }

  closeEditJsonModal(): void {
    this.isJsonModalVisible = false;
    this.isExampleJsonModal = false;
    this.jsonModalBrand = null;
    this.jsonError = '';
    this.rawJsonString = '';
  }

  prettifyJson(): void {
    try {
      const parsed = JSON.parse(this.rawJsonString);
      this.rawJsonString = JSON.stringify(parsed, null, 2);
      this.jsonError = '';
    } catch (e: any) {
      this.jsonError = 'Invalid JSON: ' + (e?.message || 'Syntax error');
    }
  }

  copyJsonToClipboard(): void {
    if (!this.rawJsonString) return;
    navigator.clipboard.writeText(this.rawJsonString).then(() => {
      this.isCopied = true;
      this.sharedService.showSuccess('JSON copied to clipboard');
      setTimeout(() => (this.isCopied = false), 2000);
    });
  }

  downloadJson(): void {
    if (!this.rawJsonString) return;
    const blob = new Blob([this.rawJsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(this.jsonModalBrand?.name || 'brand').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-branding.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  triggerEditJsonFileInput(): void {
    if (this.editJsonFileInput) {
      this.editJsonFileInput.nativeElement.value = '';
      this.editJsonFileInput.nativeElement.click();
    }
  }

  onEditJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      const file = input.files[0];
      const reader = new FileReader();
      reader.onload = (e: any) => {
        try {
          const text = e.target.result as string;
          const parsed = JSON.parse(text);
          this.rawJsonString = JSON.stringify(parsed, null, 2);
          this.jsonError = '';
          this.sharedService.showSuccess(`Loaded ${file.name} into editor`);
          this.cdRef.detectChanges();
        } catch (err: any) {
          this.jsonError = 'Invalid JSON file: ' + (err?.message || 'Syntax error');
          this.cdRef.detectChanges();
        }
      };
      reader.readAsText(file);
    }
  }

  saveBrandJson(): void {
    if (!this.rawJsonString) return;
    this.jsonError = '';

    let parsed: any;
    try {
      parsed = JSON.parse(this.rawJsonString);
    } catch (e: any) {
      this.jsonError = 'Invalid JSON syntax: ' + (e?.message || '');
      return;
    }

    if (!parsed || typeof parsed !== 'object') {
      this.jsonError = 'JSON root must be an object';
      return;
    }

    // Apply values to brandForm
    this.applyJsonToForm(parsed);

    // If standalone modal (not opened from Create/Edit form), set editingBrand if target exists
    if (!this.isBrandModalVisible) {
      if (this.jsonModalBrand && this.jsonModalBrand.id) {
        this.editingBrand = this.jsonModalBrand;
      } else {
        this.editingBrand = null;
      }
    }

    // Close the JSON editor modal
    this.isJsonModalVisible = false;

    // Directly save and create/update brand on server
    this.saveBrand();
  }

  openUploadJsonModal(brand: BrandItem): void {
    this.uploadJsonBrand = brand;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
    this.isUploadJsonModalVisible = true;
  }

  closeUploadJsonModal(): void {
    this.isUploadJsonModalVisible = false;
    this.uploadJsonBrand = null;
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
    this.isDragging = false;
  }

  triggerJsonFileInput(): void {
    if (this.jsonFileInput) {
      this.jsonFileInput.nativeElement.value = '';
      this.jsonFileInput.nativeElement.click();
    }
  }

  onJsonFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.processUploadedFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDropJsonFile(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      this.processUploadedFile(event.dataTransfer.files[0]);
    }
  }

  processUploadedFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.json') && file.type !== 'application/json') {
      this.uploadJsonError = 'Please choose a valid .json file';
      return;
    }

    this.uploadedJsonFileName = file.name;
    this.uploadJsonError = '';
    const reader = new FileReader();
    reader.onload = (e: any) => {
      try {
        const text = e.target.result as string;
        const parsed = JSON.parse(text);
        if (!parsed || typeof parsed !== 'object') {
          throw new Error('Root JSON must be an object');
        }
        this.uploadedJsonData = parsed;
        this.cdRef.detectChanges();
      } catch (err: any) {
        this.uploadedJsonData = null;
        this.uploadJsonError = 'Invalid JSON: ' + (err?.message || 'Syntax error');
        this.cdRef.detectChanges();
      }
    };
    reader.readAsText(file);
  }

  clearUploadedJsonFile(): void {
    this.uploadedJsonFileName = '';
    this.uploadedJsonData = null;
    this.uploadJsonError = '';
  }

  saveUploadedJson(): void {
    if (!this.uploadedJsonData) return;
    const parsed = this.uploadedJsonData;

    // Apply values to brandForm
    this.applyJsonToForm(parsed);

    if (!this.isBrandModalVisible) {
      if (this.uploadJsonBrand && this.uploadJsonBrand.id) {
        this.editingBrand = this.uploadJsonBrand;
      } else {
        this.editingBrand = null;
      }
    }

    // Close upload modal
    this.isUploadJsonModalVisible = false;

    // Directly save and create/update brand on server
    this.saveBrand();
  }
}
